import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Search, X, Loader2, CornerDownLeft } from "lucide-react";
import { api } from "@/lib/api";

const TYPE_STYLE = {
  Jugador: "bg-navy text-white",
  "Álbum": "bg-gold text-navy",
  Producto: "bg-[#c0392b] text-white",
  "Comisión": "bg-[#16235a] text-white",
  "Página": "bg-gray-200 text-navy",
};

export function SearchModal({ open, onClose }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (open) { setQ(""); setResults([]); setTimeout(() => inputRef.current?.focus(), 80); }
  }, [open]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    if (open) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return;
    if (q.trim().length < 2) { setResults([]); setLoading(false); return; }
    setLoading(true);
    const t = setTimeout(() => {
      api.get(`/search?q=${encodeURIComponent(q.trim())}`)
        .then(({ data }) => setResults(data))
        .catch(() => setResults([]))
        .finally(() => setLoading(false));
    }, 260);
    return () => clearTimeout(t);
  }, [q, open]);

  const go = (url) => { onClose(); navigate(url); };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="search-modal"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          onClick={onClose}
          className="fixed inset-0 z-[100] bg-navy/60 backdrop-blur-sm flex items-start justify-center pt-[12vh] px-4"
        >
          <motion.div
            initial={{ scale: 0.97, y: -10, opacity: 0 }} animate={{ scale: 1, y: 0, opacity: 1 }} exit={{ scale: 0.97, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-xl bg-white rounded-2xl shadow-2xl overflow-hidden"
          >
            <div className="flex items-center gap-3 px-4 border-b border-gray-100">
              <Search className="h-5 w-5 text-gray-400 shrink-0" />
              <input
                ref={inputRef}
                data-testid="search-input"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Buscar jugadores, álbumes, productos, secciones…"
                className="flex-1 py-4 text-base text-navy placeholder-gray-400 focus:outline-none"
              />
              {loading && <Loader2 className="h-4 w-4 animate-spin text-navy/40" />}
              <button onClick={onClose} aria-label="Cerrar" className="h-8 w-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-500">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="max-h-[55vh] overflow-y-auto">
              {q.trim().length < 2 ? (
                <p className="px-5 py-8 text-center text-sm text-gray-400">Escribí al menos 2 letras para buscar.</p>
              ) : !loading && results.length === 0 ? (
                <p data-testid="search-empty" className="px-5 py-8 text-center text-sm text-gray-400">No encontramos resultados para “{q}”.</p>
              ) : (
                <ul className="py-2">
                  {results.map((r, i) => (
                    <li key={i}>
                      <button
                        data-testid={`search-result-${i}`}
                        onClick={() => go(r.url)}
                        className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-[#eef1f7] transition-colors text-left"
                      >
                        <div className="h-10 w-10 rounded-lg overflow-hidden bg-[#eef1f7] shrink-0 flex items-center justify-center">
                          {r.image ? <img src={r.image} alt="" className="w-full h-full object-cover" /> : <Search className="h-4 w-4 text-navy/30" />}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-semibold text-navy truncate">{r.title}</div>
                          {r.subtitle && <div className="text-xs text-gray-400 truncate">{r.subtitle}</div>}
                        </div>
                        <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full ${TYPE_STYLE[r.type] || "bg-gray-200 text-navy"}`}>{r.type}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="px-4 py-2.5 border-t border-gray-100 bg-[#f7f9fc] flex items-center gap-2 text-[11px] text-gray-400">
              <CornerDownLeft className="h-3.5 w-3.5" /> Elegí un resultado · <kbd className="font-sans">Esc</kbd> para cerrar
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
